import { type FC } from 'react'
import { Button } from '../../components/Button/Button'
import { LinkButton } from '../../components/Button/LinkButton'
import { IntegrationIcon } from '../../components/IntegrationIcon'
import { IntegrationServiceId } from '../../common/integrations'
import { SchedulingStatusCard } from './SchedulingStatusCard'
import { SelectCalendarState } from './SelectCalendarState'
import { type SchedulingPageStatus } from './useSchedulingPageStatus'

const integrationConnectPath = (serviceId: IntegrationServiceId) => `/account/integration-settings/integrations/${serviceId}/settings?connect`

interface SchedulingBlockedStateProps {
  status: SchedulingPageStatus
  onRefresh: () => void
}

export const SchedulingBlockedState: FC<SchedulingBlockedStateProps> = ({ status, onRefresh }) => {
  switch (status.kind) {
    case 'error':
      return (
        <SchedulingStatusCard
          icon={<i className='bi bi-exclamation-triangle-fill text-danger' />}
          title="Couldn't load Scheduling"
          subtitle='Something went wrong on our end. Refresh to try again.'
        >
          <Button variant='primary' onClick={onRefresh}>
            Refresh
          </Button>
        </SchedulingStatusCard>
      )
    case 'connect-calendar':
      return (
        <SchedulingStatusCard
          icon={<i className='bi bi-calendar zocks-text-gray-600' />}
          title='Connect your calendar'
          subtitle='Scheduling books meetings straight into your calendar. Connect one to get started.'
        >
          <LinkButton variant='secondary' to={integrationConnectPath(IntegrationServiceId.GOOGLE_CALENDAR)}>
            <IntegrationIcon serviceId={IntegrationServiceId.GOOGLE_CALENDAR} width={18} height={18} className='me-2' />
            Google Calendar
          </LinkButton>
          <LinkButton variant='secondary' to={integrationConnectPath(IntegrationServiceId.OUTLOOK_CALENDAR)}>
            <IntegrationIcon serviceId={IntegrationServiceId.OUTLOOK_CALENDAR} width={18} height={18} className='me-2' />
            Outlook Calendar
          </LinkButton>
        </SchedulingStatusCard>
      )
    case 'select-calendar':
      return <SelectCalendarState />
    case 'reconnect-calendar':
      return (
        <SchedulingStatusCard
          icon={<i className='bi bi-exclamation-triangle-fill text-warning' />}
          title='Reconnect your calendar'
          subtitle='Scheduling paused because your calendar disconnected. Reconnect to keep taking bookings.'
        >
          <LinkButton variant='primary' to={integrationConnectPath(status.serviceId ?? IntegrationServiceId.GOOGLE_CALENDAR)}>
            Reconnect calendar
          </LinkButton>
        </SchedulingStatusCard>
      )
    case 'no-account-event-types':
      return (
        <SchedulingStatusCard
          icon={<i className='bi bi-calendar zocks-text-gray-600' />}
          title={status.isAdmin ? 'Set up event types for your account' : "Event types aren't set up yet"}
          subtitle={
            status.isAdmin
              ? 'Create shared event types so your team can start taking bookings.'
              : 'Scheduling needs event types before you can take bookings. These are managed at the account level.'
          }
        >
          {status.isAdmin ? (
            <LinkButton variant='primary' to='/workspace/setup/scheduling'>
              Set up event types
            </LinkButton>
          ) : null}
        </SchedulingStatusCard>
      )
    case 'no-user-page-event-types':
      return (
        <SchedulingStatusCard
          icon={<i className='bi bi-calendar zocks-text-gray-600' />}
          title='Add event types to your booking page'
          subtitle='Choose which event types clients can book with you. You can add them in Profile.'
        >
          <LinkButton variant='primary' to='/account/profile-settings/scheduling'>
            Go to Profile
          </LinkButton>
        </SchedulingStatusCard>
      )
    default:
      return null
  }
}
